import React from 'react';
import { i18n } from 'shared/i18n';

interface Page {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

interface Props {
  page: Page;
  onPaginate: (page: number) => void;
  updatePageSize?: (size: number) => void;
  pageSize?: number;
  showTotalElements: boolean;
  maxItems: number;
  ellipseItems: number;
}

type State = Record<string, never>;

class Pagination extends React.Component<Props, State> {
  static defaultProps = {
    showTotalElements: false,
    page: { totalPages: -1, totalElements: 10, number: 0, size: 10 },
    maxItems: 10,
    ellipseItems: 3,
  };

  constructor(props: Props) {
    super(props);

    this.onPaginate = this.onPaginate.bind(this);
  }

  onPaginate(page: number, e?: React.MouseEvent) {
    if (e) {
      e.preventDefault();
    }
    this.props.onPaginate(page);
  }

  showPageSizePicker() {
    return this.props.updatePageSize && typeof this.props.updatePageSize === 'function' && this.props.pageSize;
  }

  renderPaginatorButton(pageNo: number) {
    const active = pageNo === this.props.page.number ? 'active page-item' : 'page-item';

    return (
      <li key={'p-' + pageNo} className={active}>
        <a href="/#" className="page-link" onClick={this.onPaginate.bind(this, pageNo)}>
          {pageNo + 1}
        </a>
      </li>
    );
  }

  renderPrevButton() {
    const onClick = this.onPaginate.bind(this, this.props.page.number - 1);
    if (this.props.page.number > 0) {
      return (
        <li key="prev" className="page-item">
          <a href="/#" className="page-link pagination-arrow" onClick={onClick}>
            <i className="fa fa-angle-left" />
          </a>
        </li>
      );
    } else {
      return (
        <li key="prev" className="disabled page-item">
          <a href="/#" className="page-link pagination-arrow">
            <i className="fa fa-angle-left" />
          </a>
        </li>
      );
    }
  }

  renderNextButton() {
    const onClick = this.onPaginate.bind(this, this.props.page.number + 1);
    if (this.props.page.number + 1 < this.props.page.totalPages) {
      return (
        <li className="page-item" key="next">
          <a href="/#" className="page-link pagination-arrow" onClick={onClick}>
            <i className="fa fa-angle-right" />
          </a>
        </li>
      );
    } else {
      return (
        <li key="next" className="disabled page-item">
          <a href="/#" className="page-link pagination-arrow">
            <i className="fa fa-angle-right" />
          </a>
        </li>
      );
    }
  }

  renderPagination() {
    // No paginator at all if only 1 (or 0) pages unless we show page size picker
    if (this.props.page.totalPages < 2 && !this.showPageSizePicker()) {
      return null;
    }

    // Show ellipsis if current number is higher than ellipsisItems + 1
    const showStartEllipsis = this.props.page.totalPages > this.props.maxItems && this.props.page.number > this.props.ellipseItems + 1;
    // Show ellipsis if current number is lower than totalPages - ellipsisItems - 2
    const showFinishEllipsis =
      this.props.page.totalPages > this.props.maxItems && this.props.page.number < this.props.page.totalPages - this.props.ellipseItems - 2;
    // Start page is 0 if current number is lower than ellipsisItems + 1
    const startPage =
      this.props.page.totalPages <= this.props.maxItems || this.props.page.number <= this.props.ellipseItems + 1
        ? 0
        : this.props.page.number - this.props.ellipseItems;
    // If we show finishEllipsis, finish page is current number + ellipsisItems + 1, otherwise totalPage
    const finishPage = showFinishEllipsis ? this.props.page.number + this.props.ellipseItems + 1 : this.props.page.totalPages;

    // construct the button array now
    const buttons = [];
    if (showStartEllipsis) {
      buttons.push(this.renderPaginatorButton(0));
      buttons.push(
        <li className="disabled page-item" key="start_ellipsis">
          <a href="/" className="page-link">
            ...
          </a>
        </li>
      );
    }

    for (let i = startPage; i < finishPage; i++) {
      buttons.push(this.renderPaginatorButton(i));
    }

    if (showFinishEllipsis) {
      buttons.push(
        <li className="disabled page-item" key="end-ellipsis">
          <a href="/" className="page-link">
            ...
          </a>
        </li>
      );
      buttons.push(this.renderPaginatorButton(this.props.page.totalPages - 1));
    }

    return (
      <div>
        {this.props.showTotalElements ? (
          <p className="pagination-total">
            {i18n('table:pagination.totalElements')} {this.props.page.totalElements}
          </p>
        ) : null}
        <ul className="pagination">
          <li key="first" className={this.props.page.number === 0 ? 'disabled page-item' : 'page-item'}>
            <a href="/" className="page-link pagination-arrow" onClick={this.onPaginate.bind(this, 0)}>
              <i className="fa fa-angle-double-left" />
            </a>
          </li>
          {this.renderPrevButton()}
          {buttons}
          {this.renderNextButton()}
          <li key="last" className={this.props.page.number + 1 === this.props.page.totalPages ? 'disabled page-item' : 'page-item'}>
            <a href="/" className="page-link pagination-arrow" onClick={this.onPaginate.bind(this, this.props.page.totalPages - 1)}>
              <i className="fa fa-angle-double-right" />
            </a>
          </li>
        </ul>
      </div>
    );
  }

  override render() {
    return <div className="pagination-wrapper">{this.renderPagination()}</div>;
  }
}

export default Pagination;
